import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from '@environments/environment';
import { console } from '@app/shared/util/console.util';
import { fetchWithRetry, clearGisAuthRetryCounter, incrementGisAuthRetryCounter } from '@app/modules/gis/gis-token';
import { applyEsriTokenPatch } from '@app/modules/gis/esri-token-patch';
import 'hammerjs';

/**
 * PKCE token exchange — runs BEFORE Angular (and MSAL) bootstraps.
 *
 * When the GIS Portal redirects back after authorization it appends
 * ?code=AUTH_CODE as a query parameter (not a hash fragment, so MSAL is
 * unaffected). We intercept it here, exchange the code for tokens via a
 * direct POST to the Portal token endpoint, persist them in sessionStorage,
 * and clean the URL before handing control to Angular.
 *
 * On successful exchange, clears the retry counter (full auth succeeded).
 * On permanent auth failure (no access_token), increments retry counter.
 * Transient errors (network, 5xx, 429) are handled by fetchWithRetry.
 */
async function exchangeGisPkceCode(): Promise<void> {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  if (!code) { return; }

  const verifier   = sessionStorage.getItem('geobrain_gis_code_verifier');
  const portalUrl  = sessionStorage.getItem('geobrain_gis_portal_url');
  const appId      = sessionStorage.getItem('geobrain_gis_app_id');

  // Always clear these one-time-use keys from sessionStorage
  sessionStorage.removeItem('geobrain_gis_code_verifier');
  sessionStorage.removeItem('geobrain_gis_portal_url');
  sessionStorage.removeItem('geobrain_gis_app_id');

  // Only clean the URL after confirming this ?code= belongs to us.
  // If there is no GIS verifier the code is not ours — leave the URL intact
  // so any other handler (e.g. MSAL) can still read it.
  if (!verifier || !portalUrl || !appId) { return; }

  history.replaceState(null, '', window.location.pathname);

  try {
    const body = new URLSearchParams({
      client_id: appId,
      code,
      redirect_uri: window.location.origin + '/',
      grant_type: 'authorization_code',
      code_verifier: verifier
    });

    const res = await fetchWithRetry(`${portalUrl}/sharing/oauth2/token?f=json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    });

    const data = await res.json();
    if (data.access_token) {
      sessionStorage.setItem('geobrain_gis_access_token', data.access_token);
      sessionStorage.setItem('geobrain_gis_token_expiry', (Date.now() + data.expires_in * 1000).toString());
      if (data.refresh_token) {
        sessionStorage.setItem('geobrain_gis_refresh_token', data.refresh_token);
        if (data.refresh_token_expires_in) {
          sessionStorage.setItem('geobrain_gis_refresh_token_expiry',
            (Date.now() + data.refresh_token_expires_in * 1000).toString());
        }
      }
      // Clear retry counter on successful exchange
      clearGisAuthRetryCounter();
    } else {
      console.log('GIS PKCE token exchange failed: no access_token in response:', data);
      // Permanent auth failure (invalid code, expired code, etc.) — increment retry counter
      incrementGisAuthRetryCounter();
    }
  } catch (err) {
    // All retries exhausted (network failure, timeout, etc.) — still counts as a failed attempt
    console.log('GIS PKCE token exchange failed after retries:', err);
    incrementGisAuthRetryCounter();
  }

  // Restore the URL the user was on before the PKCE redirect
  const returnUrl = sessionStorage.getItem('geobrain_gis_return_url') || '/';
  sessionStorage.removeItem('geobrain_gis_return_url');
  history.replaceState(null, '', returnUrl);
}

(async () => {
  await exchangeGisPkceCode();

  applyEsriTokenPatch();

  if (environment.production) {
    enableProdMode();
  }

  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.log(err));
})();
